import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@store/authStore';
import { MainLayout } from '@components/layout/MainLayout';
import { AuthLayout } from '@components/layout/AuthLayout';
import { PublicLayout } from '@components/layout/PublicLayout';

// Auth Pages
import LoginPage from '@pages/auth/Login';
import RegisterPage from '@pages/auth/Register';
import ForgotPasswordPage from '@pages/auth/ForgotPassword';
import ResetPasswordPage from '@pages/auth/ResetPassword';

// Dashboard
import DashboardPage from '@pages/dashboard/Dashboard';

// Events
import EventsListPage from '@pages/events/EventsList';
import EventCreatePage from '@pages/events/EventCreate';
import EventEditPage from '@pages/events/EventEdit';
import EventDetailPage from '@pages/events/EventDetail';

// Tickets
import TicketsListPage from '@pages/tickets/TicketsList';
import TicketTypesPage from '@pages/tickets/TicketTypes';
import TicketTemplatesPage from '@pages/tickets/TicketTemplates';

// Orders
import OrdersListPage from '@pages/orders/OrdersList';
import OrderDetailPage from '@pages/orders/OrderDetail';

// Customers
import CustomersListPage from '@pages/customers/CustomersList';
import CustomerDetailPage from '@pages/customers/CustomerDetail';

// Check-in
import CheckinPage from '@pages/checkin/Checkin';
import ScannerPage from '@pages/checkin/Scanner';

// Reports
import ReportsPage from '@pages/reports/Reports';
import SalesReportPage from '@pages/reports/SalesReport';

// Settings
import SettingsPage from '@pages/settings/Settings';
import BrandSettingsPage from '@pages/settings/BrandSettings';
import PaymentSettingsPage from '@pages/settings/PaymentSettings';
import TeamSettingsPage from '@pages/settings/TeamSettings';

// Public Pages
import PublicHomePage from '@pages/public/Home';
import PublicEventsPage from '@pages/public/Events';
import PublicEventDetailPage from '@pages/public/EventDetail';
import CheckoutPage from '@pages/public/Checkout';
import CheckoutCallbackPage from '@pages/public/CheckoutCallback';
import OrderConfirmationPage from '@pages/public/OrderConfirmation';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isHydrated } = useAuthStore();

  // Wait for Zustand to rehydrate from localStorage before deciding
  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { fetchProfile, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
    }
  }, [isAuthenticated, fetchProfile]);

  return (
    <Routes>
      {/* ===== Public Storefront Routes ===== */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<PublicHomePage />} />
        <Route path="/events" element={<PublicEventsPage />} />
        <Route path="/events/:slug" element={<PublicEventDetailPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/callback" element={<CheckoutCallbackPage />} />
        <Route path="/order/:reference" element={<OrderConfirmationPage />} />
      </Route>

      {/* ===== Auth Routes ===== */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* ===== Admin Routes (Protected) ===== */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />

        <Route path="events" element={<EventsListPage />} />
        <Route path="events/create" element={<EventCreatePage />} />
        <Route path="events/:id/edit" element={<EventEditPage />} />
        <Route path="events/:id" element={<EventDetailPage />} />

        <Route path="tickets" element={<TicketsListPage />} />
        <Route path="ticket-types" element={<TicketTypesPage />} />
        <Route path="ticket-templates" element={<TicketTemplatesPage />} />

        <Route path="orders" element={<OrdersListPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />

        <Route path="customers" element={<CustomersListPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />

        <Route path="checkin" element={<CheckinPage />} />
        <Route path="scanner" element={<ScannerPage />} />

        <Route path="reports" element={<ReportsPage />} />
        <Route path="reports/sales" element={<SalesReportPage />} />

        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/brand" element={<BrandSettingsPage />} />
        <Route path="settings/payments" element={<PaymentSettingsPage />} />
        <Route path="settings/team" element={<TeamSettingsPage />} />
      </Route>

      {/* Legacy redirects */}
      <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
